import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, CircularProgress, TextField, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, Search, SearchX, X } from 'lucide-react';
import { useAuthStore } from 'store/authStore';
import { listScenarios } from 'services/scenariosApi';
import type { BackendScenario } from 'services/scenariosApi';
import type { Scenario } from 'types';
import { getScenarioRoleKey } from 'utils/scenarioRole';
import FlipCard from 'components/FlipCard';

const ROLE_LABELS: Record<string, string> = {
  lawyer: 'Юридический отдел',
  hr: 'HR-отдел',
  ops: 'Операционный менеджер',
  operation_manager: 'Операционный менеджер',
  admin: 'Администратор',
};

const ROLE_BADGE_LABELS: Record<string, string> = {
  lawyer: 'Юридический',
  hr: 'HR-отдел',
  ops: 'Операционный',
  operation_manager: 'Операционный',
};

function backendToScenario(s: BackendScenario, index: number): Scenario {
  return {
    id: s.id,
    number: String(index + 1).padStart(2, '0'),
    title: s.title,
    shortDescription: s.description ?? '',
    steps: s.action
      ? s.action
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .map((label) => ({ label }))
      : [],
    result: s.result ?? '',
    instruction: s.instruction ?? '',
    systemPrompt: s.system_prompt ?? null,
    model: s.model ?? null,
    minFiles: s.min_files ?? 0,
    maxFiles: s.max_files ?? 5,
    roles: [getScenarioRoleKey(s)],
    roleTitle: s.role?.title ?? undefined,
  };
}

export default function ScenariosPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);
    listScenarios(user.role === 'admin' ? undefined : user.roleId)
      .then((res) => {
        if (res?.items) {
          setScenarios(res.items.map((s, i) => backendToScenario(s, i)));
        }
      })
      .finally(() => setIsLoading(false));
  }, [user]);

  // useDeferredValue: поле ввода мгновенное, тяжёлый ререндер списка идёт фоновым приоритетом
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = deferredQuery.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalizedQuery) return scenarios;
    return scenarios.filter((s) =>
      [s.title, s.shortDescription, ...s.steps.map((step) => step.label)]
        .join(' ')
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [scenarios, normalizedQuery]);

  // Стабильные хендлеры — чтобы memo(FlipCard) не ломался из-за новых ссылок на каждый ререндер
  const openScenario = useCallback((id: string) => navigate(`/scenarios/${id}`), [navigate]);
  const editScenario = useCallback((id: string) => navigate(`/scenarios/${id}/edit`), [navigate]);

  if (!user) return null;

  const firstName = user.name.split(' ')[1] || user.name.split(' ')[0];

  return (
    <Box sx={{ p: { xs: 2, md: '32px 48px' }, maxWidth: 1600, mx: 'auto', width: '100%' }}>
      {/* Page header */}
      <Box
        sx={{
          mb: 4,
          pb: 3.5,
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: '-0.5px',
              color: 'var(--color-text-primary)',
              lineHeight: 1.2,
              mb: 0.625,
            }}
          >
            Добрый день, {firstName}
          </Typography>
          <Typography
            sx={{ fontSize: 13.5, color: 'var(--color-text-muted)', letterSpacing: '-0.01em' }}
          >
            {ROLE_LABELS[user.role]}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.875,
              background: 'rgba(20,116,255,0.06)',
              border: '1px solid rgba(20,116,255,0.20)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'var(--color-primary)',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--color-primary)',
                letterSpacing: '-0.01em',
              }}
            >
              {filtered.length}{' '}
              {filtered.length === 1
                ? 'сценарий доступен'
                : filtered.length >= 2 && filtered.length <= 4
                  ? 'сценария доступны'
                  : 'сценариев доступны'}
            </Typography>
          </Box>

          {user.role === 'admin' && (
            <Button
              onClick={() => navigate('/scenarios/new')}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                px: 2,
                py: 0.875,
                bgcolor: 'var(--color-primary)',
                color: '#fff',
                borderRadius: 'var(--radius-lg)',
                textTransform: 'none',
                lineHeight: 1.5,
                minWidth: 0,
                '&:hover': {
                  bgcolor: 'var(--color-primary)',
                  filter: 'brightness(0.88)',
                },
              }}
            >
              + Создать сценарий
            </Button>
          )}
        </Box>
      </Box>

      {/* Loading state */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : scenarios.length === 0 ? (
        /* Empty state */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pt: 10,
            pb: 6,
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'rgba(20,116,255,0.07)',
              border: '1px solid rgba(20,116,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-primary)',
              opacity: 0.7,
            }}
          >
            <LayoutGrid size={28} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-secondary)', mb: 0.5 }}
            >
              Сценарии не найдены
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              {user.role === 'admin'
                ? 'Создайте первый сценарий, чтобы начать работу'
                : 'Сценарии для вашей роли ещё не настроены'}
            </Typography>
          </Box>
          {user.role === 'admin' && (
            <Button
              onClick={() => navigate('/scenarios/new')}
              variant="contained"
              sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
            >
              + Создать сценарий
            </Button>
          )}
        </Box>
      ) : (
        <>
          {/* Search */}
          <Box sx={{ mb: 3 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Поиск по сценариям…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setQuery('');
              }}
              sx={{
                maxWidth: 480,
                '& .MuiOutlinedInput-root': {
                  fontSize: 13.5,
                  bgcolor: '#fff',
                  borderRadius: 'var(--radius-lg)',
                  '& fieldset': { borderColor: 'var(--color-border)' },
                  '&:hover fieldset': { borderColor: 'rgba(20,116,255,0.40)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', mr: 1, color: 'var(--color-text-muted)' }}>
                    <Search size={16} />
                  </Box>
                ),
                endAdornment: query ? (
                  <IconButton
                    size="small"
                    aria-label="Очистить поиск"
                    onClick={() => setQuery('')}
                    sx={{
                      color: 'var(--color-text-muted)',
                      '&:hover': { color: 'var(--color-primary)' },
                    }}
                  >
                    <X size={15} />
                  </IconButton>
                ) : null,
              }}
            />
          </Box>

          {filtered.length === 0 ? (
            /* No search matches */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pt: 8,
                pb: 6,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'rgba(20,116,255,0.07)',
                  border: '1px solid rgba(20,116,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-primary)',
                  opacity: 0.7,
                }}
              >
                <SearchX size={28} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: 'var(--color-text-secondary)',
                    mb: 0.5,
                  }}
                >
                  Ничего не найдено
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                  По запросу «{deferredQuery.trim()}» сценариев нет
                </Typography>
              </Box>
              <Button
                onClick={() => setQuery('')}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  color: 'var(--color-primary)',
                }}
              >
                Сбросить поиск
              </Button>
            </Box>
          ) : user.role === 'admin' ? (
            /* Admin: grouped by role */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(
                filtered.reduce<Record<string, Scenario[]>>((acc, s) => {
                  const role = s.roles[0] ?? 'unknown';
                  (acc[role] ??= []).push(s);
                  return acc;
                }, {})
              ).map(([role, items]) => (
                <Box key={role}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2,
                      pb: 1.25,
                      borderBottom: '1px solid var(--color-border)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      {ROLE_LABELS[role] ?? items[0]?.roleTitle ?? role}
                    </Typography>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        bgcolor: 'rgba(20,116,255,0.07)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: 'var(--color-primary)',
                      }}
                    >
                      {items.length}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2.5,
                    }}
                  >
                    {items.map((scenario) => (
                      <FlipCard
                        key={scenario.id}
                        scenario={scenario}
                        onOpen={openScenario}
                        onEdit={editScenario}
                        roleLabelOverride={ROLE_BADGE_LABELS[scenario.roles[0]] ?? undefined}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            /* Regular user: flat grid */
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2.5,
              }}
            >
              {filtered.map((scenario) => (
                <FlipCard key={scenario.id} scenario={scenario} onOpen={openScenario} />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
